import { CardComponentProps } from "@~/types/types";
import {
  PrimaryButton as V31PrimaryButton,
  SecondaryButton as V31SecondaryButton,
} from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import {
  PrimaryButton as DeprecatedPrimaryButton,
  SecondaryButton as DeprecatedSecondaryButton,
} from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { useFeatureFlags } from "@~/flags/flags";
import styles from "./CallToAction.module.scss";
import {
  getProductUrl,
  isAddToBasketProduct,
  isEmailWhenBackInStockProduct,
  isInStoreOnlyProduct,
  isMultiSkuProduct,
  isPermanentlyOutOfStockProduct,
  isPreOrderWithoutNotifiableEventsProduct,
  isProductSetProduct,
  isShipFromStoreProduct,
  isViewOptionsProduct,
} from "../../utils/product";
import { useEmailConfirmed } from "@~/product-card-components/__shared__/AddToBasketUtils/hooks/useEmailConfirmed";
import { QuickViewModal } from "../__shared__/QuickViewModal/QuickViewModal";
import { useState } from "react";
import { useWindowSize } from "../../utils/hooks/useWindowSize";
import { useVariant } from "@~/contexts/VariantContext/VariantContext";
import { useProductCardEvent } from "@~/contexts/ProductCardEventContext/ProductCardEventContext";
import { productCardEvents } from "@~/contexts/ProductCardEventContext/productCardEvents";
import { BasketButton } from "./BasketButton";

export function CallToAction({
  product,
  configProps,
}: Pick<CardComponentProps, "product" | "configProps">) {
  const buttonContent = CallToActionButton({ product, configProps });

  if (buttonContent === null) return null;

  return <div data-component="pcl-CallToAction">{buttonContent}</div>;
}

export function CallToActionButton({
  product,
  configProps,
}: Pick<CardComponentProps, "product" | "configProps">) {
  const [showQVModal, setShowQVModal] = useState(false);
  const { variant } = useVariant();
  const dispatchEvent = useProductCardEvent();

  const { isJldsV31Enabled: jldsV31Enabled } = useFeatureFlags();
  const isJldsV31Enabled = jldsV31Enabled?.value;

  const { width: windowWidth } = useWindowSize();
  const isSmallScreen = windowWidth ? windowWidth < 768 : false;
  const desktopQuickViewConfig =
    typeof configProps?.quickViewDesktopConfig === "string" &&
    configProps.quickViewDesktopConfig.length > 0
      ? configProps.quickViewDesktopConfig
      : (configProps?.qvConfig as string | undefined) || "standard";
  const { pdpURL: { url } = {}, title } = product;
  const isMultiSku = isMultiSkuProduct(product);
  const isProductSet = isProductSetProduct(product);
  const clientId = configProps?.clientId as string;

  const showQuickView = () => {
    if (typeof window?.performance?.mark === "function") {
      performance.mark("quickViewDisplayed-start");
    }

    setShowQVModal(true);
  };

  const hasAddToBasketButton = !configProps?.hideAddToBasket;
  const hasGoToBasketButton = !configProps?.hideGoToBasket;
  const hasEmailWhenAvailableButton = !configProps?.hideEmailWhenAvailable;
  const hasInStoreOnlyButton = !configProps?.hideInStoreOnly;

  const hasMoreInformationButton =
    !configProps?.hideAddToBasket || isProductSet;
  const shouldUsePrimaryButton = configProps?.usePrimaryButton ?? false;

  const hasOutOfStockButton = !configProps?.hideOutOfStock;
  const hasViewOptionsButton = configProps?.showViewOptions ?? false;
  const handleMoreInformationClick = configProps?.handleMoreInformationClick as
    ((event: React.MouseEvent<Element>) => void) | undefined;

  const shouldShowAddToBasketButton = isAddToBasketProduct(product);
  const shouldShowEmailWhenAvailable = isEmailWhenBackInStockProduct(product);
  const shouldShowInStoreOnly = isInStoreOnlyProduct(product);
  const shouldShowPermanentlyOutOfStock =
    isPermanentlyOutOfStockProduct(product);
  const shouldShowViewOptions = isViewOptionsProduct(product);

  const hasHadEmailConfirmed = useEmailConfirmed(
    shouldShowEmailWhenAvailable,
    product?.aliases?.pimProductId,
  );

  if (hasViewOptionsButton && shouldShowViewOptions) {
    return isJldsV31Enabled ? (
      <V31SecondaryButton
        className={styles.cta_button}
        href={url}
        size="small"
        tag="a"
        labelText="View options"
        aria-label={`View options, ${title}`}
        onClick={() => {
          dispatchEvent(productCardEvents.ctaViewOptions);
        }}
      />
    ) : (
      <DeprecatedSecondaryButton
        className={styles.cta_button}
        href={url}
        size="small"
        tag="a"
        onClick={() => {
          dispatchEvent(productCardEvents.ctaViewOptions);
        }}
      >
        View options
        <span className={styles.visually_hidden}>{`, ${title}`}</span>
      </DeprecatedSecondaryButton>
    );
  }

  if (hasInStoreOnlyButton && shouldShowInStoreOnly) {
    return isJldsV31Enabled ? (
      <V31PrimaryButton
        className={styles.cta_button}
        href={url}
        size="small"
        tag="a"
        labelText="In-store only"
        onClick={() => {
          dispatchEvent(productCardEvents.ctaInStoreOnly);
        }}
      />
    ) : (
      <DeprecatedPrimaryButton
        className={styles.cta_button}
        href={url}
        size="small"
        tag="a"
        onClick={() => {
          dispatchEvent(productCardEvents.ctaInStoreOnly);
        }}
      >
        In-store only
      </DeprecatedPrimaryButton>
    );
  }

  if (hasOutOfStockButton && shouldShowPermanentlyOutOfStock) {
    return isJldsV31Enabled ? (
      <V31PrimaryButton
        className={styles.cta_button}
        href={url}
        size="small"
        tag="a"
        data-testid="Out Of Stock Button"
        labelText="Out of stock"
        onClick={() => {
          dispatchEvent(productCardEvents.ctaOutOfStock);
        }}
      />
    ) : (
      <DeprecatedPrimaryButton
        className={styles.cta_button}
        href={url}
        size="small"
        tag="a"
        data-testid="Out Of Stock Button"
        disabled
        onClick={() => {
          dispatchEvent(productCardEvents.ctaOutOfStock);
        }}
      >
        Out of stock
      </DeprecatedPrimaryButton>
    );
  }

  if (hasEmailWhenAvailableButton && shouldShowEmailWhenAvailable) {
    if (!hasHadEmailConfirmed || showQVModal) {
      return (
        <>
          {isJldsV31Enabled ? (
            <V31SecondaryButton
              className={styles.cta_button}
              size="small"
              disabled={showQVModal}
              submitting={showQVModal}
              data-testid="component-email-bis-button"
              labelText="Email when available"
              aria-label={`Email when available, ${title}`}
              onClick={() => {
                showQuickView();
                dispatchEvent(productCardEvents.ctaEmailMe);
              }}
            />
          ) : (
            <DeprecatedSecondaryButton
              className={styles.cta_button}
              size="small"
              disabled={showQVModal}
              submitting={showQVModal}
              onClick={() => {
                showQuickView();
                dispatchEvent(productCardEvents.ctaEmailMe);
              }}
              data-testid="component-email-bis-button"
            >
              Email when available
              <span className={styles.visually_hidden}>{`, ${title}`}</span>
            </DeprecatedSecondaryButton>
          )}
          <QuickViewModal
            product={product}
            showModal={showQVModal}
            onClose={() => setShowQVModal(false)}
            config={isSmallScreen ? "quickAdd" : desktopQuickViewConfig}
            originator={"ADD_TO_BASKET"}
          ></QuickViewModal>
        </>
      );
    }

    if (hasHadEmailConfirmed && !showQVModal) {
      return isJldsV31Enabled ? (
        <V31SecondaryButton
          className={styles.cta_button}
          href={url}
          size="small"
          tag="a"
          labelText="We'll be in touch"
          aria-label={`We'll be in touch, ${title}`}
          onClick={() => {
            dispatchEvent(productCardEvents.ctaWellBeInTouch);
          }}
        />
      ) : (
        <DeprecatedSecondaryButton
          className={styles.cta_button}
          href={url}
          size="small"
          tag="a"
          onClick={() => {
            dispatchEvent(productCardEvents.ctaWellBeInTouch);
          }}
        >
          We'll be in touch
        </DeprecatedSecondaryButton>
      );
    }
  }

  if (hasAddToBasketButton && shouldShowAddToBasketButton) {
    return (
      <>
        <BasketButton
          product={product}
          configProps={configProps}
          title={title}
          isJldsV31Enabled={!!isJldsV31Enabled}
          hasGoToBasketButton={hasGoToBasketButton}
          clientId={clientId}
          isMultiSku={isMultiSku}
          showQuickView={showQuickView}
        />

        {showQVModal && (
          <QuickViewModal
            product={product}
            showModal={showQVModal}
            onClose={() => setShowQVModal(false)}
            modalTitle={isSmallScreen ? "Confirm colour and size" : ""}
            config={isSmallScreen ? "quickAdd" : desktopQuickViewConfig}
            originator={"ADD_TO_BASKET"}
          />
        )}
      </>
    );
  }

  if (
    hasMoreInformationButton ||
    isPreOrderWithoutNotifiableEventsProduct(product)
  ) {
    const moreInformationLabel = configProps?.moreInformationLabel
      ? (configProps.moreInformationLabel as string)
      : isProductSet
        ? "View range"
        : "More information";

    if (shouldUsePrimaryButton) {
      return isJldsV31Enabled ? (
        <V31PrimaryButton
          data-testid={"call-to-action-more-information-primary"}
          className={styles.cta_button}
          href={getProductUrl(product, variant)}
          size="small"
          tag="a"
          labelText={moreInformationLabel}
          onClick={() => {
            dispatchEvent(productCardEvents.ctaMoreInformation);
          }}
        />
      ) : (
        <DeprecatedPrimaryButton
          data-testid={"call-to-action-more-information-primary"}
          className={styles.cta_button}
          href={getProductUrl(product, variant)}
          size="small"
          tag="a"
          onClick={() => {
            dispatchEvent(productCardEvents.ctaMoreInformation);
          }}
        >
          {moreInformationLabel}
        </DeprecatedPrimaryButton>
      );
    } else {
      const isShipFromStore = isShipFromStoreProduct(product);
      return isJldsV31Enabled ? (
        <V31SecondaryButton
          className={styles.cta_button}
          data-test-ship-from-store={isShipFromStore}
          href={getProductUrl(product, variant)}
          size="small"
          tag="a"
          labelText={moreInformationLabel}
          onClick={(e: React.MouseEvent<Element>) => {
            handleMoreInformationClick?.(e);
            dispatchEvent(productCardEvents.ctaMoreInformation);
          }}
        />
      ) : (
        <DeprecatedSecondaryButton
          className={styles.cta_button}
          data-test-ship-from-store={isShipFromStore}
          onClick={(e: React.MouseEvent<Element>) => {
            handleMoreInformationClick?.(e);
            dispatchEvent(productCardEvents.ctaMoreInformation);
          }}
          href={getProductUrl(product, variant)}
          size="small"
          tag="a"
        >
          {moreInformationLabel}
        </DeprecatedSecondaryButton>
      );
    }
  }

  return null;
}
